/* Copyright (c) 2016-2023 Planticle Pty Ltd */
"use strict";

import ko from "knockout";

function updateValue(element, value) {
  if(value === undefined || value === null) {
    value = '';
  }
  if(element.value !== value) {
    element.value = value;
  }
}

// Allows us to bind a dom node as the element content. A string value will be added as a text node
ko.bindingHandlers.domContent = {
  init: function(element, valueAccessor, allBindings, viewModel, bindingContext) {
  },
  update: function(element, valueAccessor, allBindings, viewModel, bindingContext) {
    var content = ko.unwrap(valueAccessor());
    if(typeof content === 'string') {
      element.textContent = content;
    } else if (content.parentNode != element) {
      // Clear out any existing content
      while(element.lastChild) {
        element.removeChild(element.lastChild);
      }
      element.appendChild(content);
    }
  }
}

ko.bindingHandlers.hover = {
  init: function(element, valueAccessor, allBindings, viewModel, bindingContext) {
    element.addEventListener("mouseenter", function(event) {
      valueAccessor()(true);
    });
    element.addEventListener("mouseleave", function(event) {
      valueAccessor()(false);
    });
  }
};

ko.bindingHandlers.numericInput = {
  init: function(element, valueAccessor, allBindings, viewModel, bindingContext) {
    element.addEventListener("input", function() {
      var val = element.value;
      var newVal = val.replace(/[^0-9]/g, '');

      if(allBindings.has('allowZero') && !ko.unwrap(allBindings.get('allowZero'))) {
        newVal = newVal.replace(/^0+/, '');
      }

      valueAccessor()(newVal);
      updateValue(element, newVal);
    });
  },
  update: function(element, valueAccessor, allBindings, viewModel, bindingContext) {
    var val = ko.unwrap(valueAccessor());
    updateValue(element, val);
  }
};

ko.bindingHandlers.regexInput = {
  init: function(element, valueAccessor, allBindings, viewModel, bindingContext) {
    var pattern = ko.unwrap(allBindings.get('regexInputPattern'));
    var regex = new RegExp(pattern);

    element.addEventListener("input", function() {
      var oldVal = valueAccessor()();
      var val = element.value;

      var newVal = val.match(regex) ? val : oldVal;
      newVal = (newVal || '').toUpperCase();

      valueAccessor()(newVal);
      updateValue(element, newVal);
    });
  },
  update: function(element, valueAccessor, allBindings, viewModel, bindingContext) {
    var val = ko.unwrap(valueAccessor());
    updateValue(element, val);
  }
};

ko.bindingHandlers.decimalInput = {
  init: function(element, valueAccessor, allBindings, viewModel, bindingContext) {
    var regex = /^(-?[0-9]*(\.[0-9]*)?)?$/;

    var decimalPlaces = +ko.unwrap(allBindings.get('decimalPlaces'));

    element.addEventListener("input", function() {
      var oldVal = valueAccessor()();
      var val = element.value;

      var newVal = val.match(regex) ? val : oldVal;

      if(decimalPlaces) {
        var dpIndex = newVal.indexOf('.');
        if(dpIndex >= 0) {
          newVal = newVal.substr(0, dpIndex + decimalPlaces + 1);
        }
      }

      newVal = (newVal || '').toUpperCase();

      valueAccessor()(newVal);
      updateValue(element, newVal);
    });
  },
  update: function(element, valueAccessor, allBindings, viewModel, bindingContext) {
    var val = ko.unwrap(valueAccessor());
    updateValue(element, val);
  }
};

ko.bindingHandlers.booleanValue = {
  init: function(element, valueAccessor, allBindingsAccessor) {
    var observable = valueAccessor(),
    interceptor = ko.computed({
      read: function() {
        var val = observable();
        return val == null ? "" : val.toString();
      },
      write: function(newValue) {
        observable(
          newValue === "true" ? true :
          newValue === "false" ? false :
          null
         );
      }
    });

    ko.applyBindingsToNode(element, { value: interceptor });
  }
};

ko.bindingHandlers.map = {
  init: function(element, valueAccessor, allBindings, viewModel) {
  },
  update: function(element, valueAccessor, allBindings) {
    // if(ko.unwrap(valueAccessor())) console.log("map binding is read-only; ignoring update");
  }
};

ko.bindingHandlers.mapOptions = {
  init: function(element, valueAccessor, allBindings, viewModel, bindingContext) {
    // TODO - handle disposal correctly (tricky because you can't really destroy a google map instance), or at least log a warning
    // ko.utils.domNodeDisposal.addDisposeCallback(element, function() { });
  },
  update: function(element, valueAccessor, allBindings, viewModel, bindingContext) {
    var mapOptions = ko.unwrap(valueAccessor());

    if(mapOptions) {
      if(element.getAttribute("data-map-created")) {
        ko.unwrap(allBindings.get('map')).setOptions(mapOptions);
      } else {
        var map = new google.maps.Map(element, mapOptions);
        element.setAttribute("data-map-created", "true");

        // Wait for map to have a projection before we update the 'map' binding
        let listener = google.maps.event.addListener(map, "projection_changed", function() {
          if(map.getProjection()) {
            listener.remove();
            allBindings.get('map')(map);
          }
        });
      }
    }
  }
};


function loadOnScroll(rawItems, items, scrollContainer, rowsToLoad, loadThreshold) {
  var rowsLoaded = 0;

  rawItems.subscribe(function(newValue) {
    loadInitialItems();
  });

  var throttleTimer;
  scrollContainer.addEventListener('scroll', function() {
    if(!throttleTimer) {
      loadItemsIfNecessary();
      throttleTimer = setTimeout(function() {
        loadItemsIfNecessary();
        throttleTimer = null;
      }, 300);
    }
  });

  function loadInitialItems() {
    scrollContainer.scrollTop = 0;
    rowsLoaded = Math.min(rowsToLoad, rawItems().length);
    items(rawItems().slice(0, rowsLoaded));
  }

  function loadItemsIfNecessary() {
    // console.log(scrollContainer.scrollHeight);
    // console.log(scrollContainer.clientHeight);
    // console.log(scrollContainer.scrollTop);
    var sc = scrollContainer;

    if(sc.scrollHeight - sc.scrollTop - sc.clientHeight < loadThreshold && rowsLoaded < rawItems().length) {
      var position = rowsLoaded;
      rowsLoaded = Math.min(rowsLoaded * 2, rawItems().length);
      items.pushAll(rawItems().slice(position, rowsLoaded));
    }
  }

  loadInitialItems();

  return items;
}

ko.bindingHandlers.loadOnScroll = {
  init: function(element, valueAccessor, allBindings, viewModel, bindingContext) {
    var scrollContainer = element;
    var value = ko.unwrap(valueAccessor);

    loadOnScroll(value().items, value().rendered, scrollContainer, 200, 10000);
  }
};

ko.bindingHandlers.scrollPercentage = {
  init: function(element, valueAccessor, allBindings, viewModel, bindingContext) {
    var sc = element;

    sc.addEventListener('scroll', function() {
      var percentage = 100 * Math.max(Math.min(sc.scrollTop / (sc.scrollHeight - sc.clientHeight), 1), 0);
      valueAccessor()(percentage);
    });
  },
  update: function(element, valueAccessor, allBindings, viewModel, bindingContext) {
    var sc = element;
    var percentage = ko.unwrap(valueAccessor());
    sc.scrollTop = percentage * (sc.scrollHeight - sc.clientHeight) / 100;
  }
};

function yScrollParent(el) {
  var parent = el.parentElement;
  if(!parent) return null;
  var style = window.getComputedStyle(parent, null).getPropertyValue("overflow-y");

  if(style == "auto" || style == "scroll") {
    return parent;
  } else {
    return yScrollParent(parent);
  }
}


// Works on the assumption that the nearest scrollable ancestor (i.e. with y-overflow: auto/scroll) is also
// the offset parent (i.e. position: absolute/absolute)
ko.bindingHandlers.scrollIntoView = {
  init: function(element, valueAccessor, allBindings, viewModel, bindingContext) {

  },
  update: function(element, valueAccessor, allBindings, viewModel, bindingContext) {
    var val = ko.unwrap(valueAccessor());
    if(val) {
      var scrollParent = yScrollParent(element);
      if(!scrollParent) {
        console.log("Scroll parent is null");
        return;
      }
      var y = element.offsetTop;
      var h = element.offsetHeight;

      if(y < scrollParent.scrollTop) {
        scrollParent.scrollTop = y;
      } else if(y + h > scrollParent.scrollTop + scrollParent.clientHeight) {
        scrollParent.scrollTop = y - scrollParent.clientHeight + h;
      }
    }
  }
};

ko.bindingHandlers.scrollIntoViewSimple = {
  init: function(element, valueAccessor, allBindings, viewModel, bindingContext) {

  },
  update: function(element, valueAccessor, allBindings, viewModel, bindingContext) {
    var val = ko.unwrap(valueAccessor());
    if(val && element.scrollIntoView) {
      setTimeout(function() {
        var rect = element.getBoundingClientRect();
        if(rect.top < 0) { // If top of element is above viewport
          element.scrollIntoView();
        } else if(rect.bottom > window.innerHeight) { // If bottom of element is below viewport
          element.scrollIntoView(false);
        }
      });
    }
  }
};

// Sets a javascript property of the element (e.g. for setting 'indeterminate' on a checkbox input)
ko.bindingHandlers.jsProp = {
  init: function(element, valueAccessor) {
    ko.computed({
      read: function() {
        let val = ko.unwrap(valueAccessor());
        if(typeof val === "object") {
          for(const [key, value] of Object.entries(val)) {
            element[key] = ko.unwrap(value);
          }
        }
      },
      disposeWhenNodeIsRemoved: element
    });
  }
}

// Polyfill of the class binding in recent Knockout versions
ko.bindingHandlers.class = {
  init: function(element, valueAccessor) {
    ko.computed({
      read: function() {
        let val = ko.unwrap(valueAccessor());
        if(typeof val === "object") {
          for(const [key, value] of Object.entries(val)) {
            element.classList.toggle(key, ko.unwrap(value));
          }
        }
      },
      disposeWhenNodeIsRemoved: element
    });
  }
}

// Observable extender to log changes - handy for debugging
ko.extenders.logChange = function(target, option) {
    target.subscribe(function(newValue) {
       console.log(option + ":");
       console.log(newValue);
    });
    return target;
};

ko.extenders.rate = function(target, option) {
  var obs = ko.observable(target());

  var delay = option;
  var timer;
  var changePending;

  function onChange() {
    if(timer) {
      changePending = true;
      return;
    }

    changePending = false;
    obs(target());

    timer = setTimeout(function() {
      timer = null;
      if(changePending) onChange();
    }, delay);
  }

  target.subscribe(onChange);

  return obs;
};

ko.extenders.stringify = function(target, option) {
  var json = ko.pureComputed(function() {
    return JSON.stringify(target());
  });
  return ko.pureComputed(function() {
    return JSON.parse(json());
  });
};

ko.extenders.objectEquals = function(target, option) {
  if(option) {
    target.equalityComparer = function(a, b) {
      return a === b || ko.observable.fn.equalityComparer(a, b);
    }
  }
  return target;
}

ko.observableArray.fn.pushAll = function(valuesToPush) {
    var underlyingArray = this();
    this.valueWillMutate();
    ko.utils.arrayPushAll(underlyingArray, valuesToPush);
    this.valueHasMutated();
    return this;  //optional
};

// https://github.com/knockout/knockout/issues/382#issuecomment-29656622
// http://jsfiddle.net/mbest/3kCkn/16/
ko.bindingHandlers.optionsBind = {
  preprocess: function(value, key, addBinding) {
    addBinding('optionsAfterRender', 'function(option, item) { ko.bindingHandlers.optionsBind.bind(option, item, ' + value + ') }');
  },
  bind: function(option, item, bindings) {
    if (item !== undefined) {
      option.setAttribute('data-bind', bindings);
      ko.applyBindings(ko.contextFor(option).createChildContext(item), option);
    }
  }
};
